/** What a theme puts on the helper's head. */
export type AiThemeHat =
  | 'none'
  | 'beanie'
  | 'straw'
  | 'santa'
  | 'lucia'
  | 'witch'
  | 'bunny'
  | 'flowers'
  | 'party';

/** What falls, flies or shines behind the helper. */
export type AiThemeWeather =
  | 'none'
  | 'snow'
  | 'blossoms'
  | 'sun'
  | 'leaves'
  | 'bats'
  | 'feathers'
  | 'confetti';

/**
 * A look for every AI character: its body colour, a hat and weather. Seasons
 * keep the Nodon blue; holidays may change it.
 */
export type AiTheme = {
  id: string;
  name: string;
  /** Body fill. */
  body: string;
  /** A darker shade of the body, for inner parts (the Swirl's middle). */
  shade: string;
  hat: AiThemeHat;
  weather: AiThemeWeather;
};

/**
 * Every helper's body: the logo's blue (#005EB0) at the light, muted tone of
 * the proposal colours (Henrik 2026-10-15).
 */
const HELPER_BLUE = '#6B97BC';
const HELPER_BLUE_SHADE = '#3F6C93';

const blue = { body: HELPER_BLUE, shade: HELPER_BLUE_SHADE };

export const DEFAULT_AI_THEME: AiTheme = {
  id: 'default',
  name: 'Default',
  ...blue,
  hat: 'none',
  weather: 'none',
};

/** Seasons, in calendar order. */
export const AI_SEASON_THEMES = {
  winter: {
    id: 'winter',
    name: 'Winter',
    ...blue,
    hat: 'beanie',
    weather: 'snow',
  },
  spring: {
    id: 'spring',
    name: 'Spring',
    ...blue,
    hat: 'none',
    weather: 'blossoms',
  },
  summer: {
    id: 'summer',
    name: 'Summer',
    ...blue,
    hat: 'straw',
    weather: 'sun',
  },
  autumn: {
    id: 'autumn',
    name: 'Autumn',
    ...blue,
    hat: 'none',
    weather: 'leaves',
  },
} satisfies Record<string, AiTheme>;

/** Holidays, in calendar order. */
export const AI_HOLIDAY_THEMES = {
  easter: {
    id: 'easter',
    name: 'Easter',
    body: '#E2BE4F',
    shade: '#B8952C',
    hat: 'bunny',
    weather: 'feathers',
  },
  midsummer: {
    id: 'midsummer',
    name: 'Midsummer',
    ...blue,
    hat: 'flowers',
    weather: 'blossoms',
  },
  halloween: {
    id: 'halloween',
    name: 'Halloween',
    body: '#E08A3C',
    shade: '#A85F22',
    hat: 'witch',
    weather: 'bats',
  },
  lucia: { id: 'lucia', name: 'Lucia', ...blue, hat: 'lucia', weather: 'snow' },
  christmas: {
    id: 'christmas',
    name: 'Christmas',
    ...blue,
    hat: 'santa',
    weather: 'snow',
  },
  newYear: {
    id: 'new-year',
    name: 'New Year',
    ...blue,
    hat: 'party',
    weather: 'confetti',
  },
} satisfies Record<string, AiTheme>;

/** Every theme, for pickers: the default, then seasons, then holidays. */
export const AI_THEMES: AiTheme[] = [
  DEFAULT_AI_THEME,
  ...Object.values(AI_SEASON_THEMES),
  ...Object.values(AI_HOLIDAY_THEMES),
];
